import {
	Button,
	cn,
	LogoPay,
	TopPharmacyBadge,
} from "@au-schein-de/au-ui-library";
import type { Cart } from "@dransay/api/models";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isCombinedPaymentEnabled } from "~/features/products/combined-payment.utils";
import { formatAddress } from "../features/products/filters/utils/format-address";
import { VendorDetailsPhone } from "./vendor-details.phone";

const vendorDetailsVariants = cva("flex gap-2", {
	variants: {
		variant: {
			default:
				"flex-col items-start md:flex-row md:justify-between md:items-center",
			compact: "flex-col",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

interface VendorDetailsProps {
	vendor: NonNullable<Cart["vendor"]>;
	variant?: "default" | "compact";
}

export function VendorDetails({
	vendor,
	variant = "default",
}: VendorDetailsProps) {
	const { t } = useTranslation("cart");
	const [showDetails, setShowDetails] = useState(false);

	return (
		<div className="flex flex-col bg-gray-100 p-4 rounded-xl">
			<div className={clsx(vendorDetailsVariants({ variant }))}>
				<div className="flex w-full min-w-0 items-center justify-between gap-2 md:w-auto md:justify-start md:gap-3">
					{vendor.isTop && (
						<span className="flex h-[1lh] items-center text-body-lg-emphasis">
							<TopPharmacyBadge />
						</span>
					)}
					<span className="min-w-0 flex-1 text-body-lg-emphasis text-gray-700">
						{vendor.name}
					</span>
					{isCombinedPaymentEnabled(vendor) && (
						<LogoPay
							title="DrAnsay Pay"
							className="h-5 w-9 shrink-0 self-center md:h-6 md:w-11"
							data-testid="vendor-details-combined-payment-badge"
						/>
					)}
				</div>
				<Button
					variant="ghost"
					size="fit"
					onClick={() => setShowDetails(!showDetails)}
				>
					{t(showDetails ? "hideDetails" : "showDetails")}
				</Button>
			</div>
			<div
				className={cn(
					"transition-all duration-300 overflow-hidden flex flex-col gap-2",
					showDetails ? "max-h-60 opacity-100 pt-2" : "max-h-0 opacity-0",
				)}
			>
				<div className="flex flex-col gap-2">
					<div className="flex flex-col">
						<span className="text-body-sm-emphasis">Address:</span>
						<span>{vendor.address ? formatAddress(vendor.address) : null}</span>
					</div>
					<div className="flex flex-col">
						<span className="text-body-sm-emphasis">Contact:</span>
						<VendorDetailsPhone contact={vendor.contact} />
						<span>{vendor.contact?.email}</span>
					</div>
				</div>
			</div>
		</div>
	);
}
